import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Ruler, Building2, ArrowRight, Phone } from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  land: "Land",
  pg_building: "PG / Rental Building",
  residential: "Residential",
  commercial: "Commercial",
};

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Available Properties — Urban Veera";
    axios
      .get(`${API}/properties/public`)
      .then((res) => {
        setProperties(res.data);
        setState("ready");
      })
      .catch(() => setState("error"));
    return () => {
      document.title = "Urban Veera — End-to-End Property & Construction Solutions";
    };
  }, []);

  return (
    <main className="bg-white text-navy" data-testid="properties-page">
      <Nav />

      <section className="bg-navy pb-16 pt-32 md:pt-40" data-testid="properties-hero">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber">Urban Veera Listings</p>
          <h1
            className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl"
            data-testid="properties-headline"
          >
            Available properties &amp; plots
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-300">
            Land parcels and income-ready buildings from our network — each one
            verified by our team before it reaches you.
          </p>
        </div>
      </section>

      <section className="bg-cream py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          {state === "loading" && (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse rounded-xl border border-border bg-white">
                  <div className="aspect-video rounded-t-xl bg-sand" />
                  <div className="space-y-3 p-6">
                    <div className="h-4 w-2/3 rounded bg-sand" />
                    <div className="h-3 w-1/2 rounded bg-sand" />
                    <div className="h-3 w-1/3 rounded bg-sand" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {state === "error" && (
            <div className="rounded-xl border border-border bg-white p-10 text-center" data-testid="properties-error">
              <p className="font-display text-lg font-bold text-navy">Listings couldn't load right now.</p>
              <p className="mt-2 text-sm text-gray-500">Please call us and we'll share what's available.</p>
              <a
                href="tel:+918050444041"
                data-testid="properties-error-call"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-amber hover:text-navy"
              >
                <Phone size={14} /> +91 80504 44041
              </a>
            </div>
          )}

          {state === "ready" && properties.length === 0 && (
            <div className="rounded-xl border border-border bg-white p-10 text-center" data-testid="properties-empty">
              <Building2 size={28} className="mx-auto text-amberdark" />
              <p className="mt-4 font-display text-lg font-bold text-navy">New listings are on the way.</p>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-500">
                Tell us what you're looking for — plot, rental building or
                commercial space — and we'll call you first when a match comes in.
              </p>
              <a
                href="/#contact-form"
                data-testid="properties-empty-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-amber px-6 py-3 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white"
              >
                Tell us your requirement <ArrowRight size={15} />
              </a>
            </div>
          )}

          {state === "ready" && properties.length > 0 && (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-grid">
              {properties.map((p) => (
                <article
                  key={p.id}
                  data-testid={`property-card-${p.id}`}
                  className="flex flex-col overflow-hidden rounded-xl border border-border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  {p.photo_path ? (
                    <img
                      src={`${API}/public/files/${p.photo_path}`}
                      alt={p.name}
                      data-testid={`property-photo-${p.id}`}
                      className="aspect-video w-full object-cover"
                    />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center bg-navy" data-testid={`property-photo-${p.id}`}>
                      <Building2 size={36} className="text-amber" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="inline-block w-fit rounded-full bg-cream px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amberdark">
                      {TYPE_LABELS[p.type] || p.type}
                    </span>
                    <h3 className="mt-3 font-display text-lg font-bold tracking-tight text-navy" data-testid={`property-name-${p.id}`}>
                      {p.name}
                    </h3>
                    <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-500">
                      <MapPin size={14} className="shrink-0 text-amberdark" />
                      {[p.location, p.city].filter(Boolean).join(", ") || "Location on enquiry"}
                    </p>
                    {p.size_sqft ? (
                      <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-500">
                        <Ruler size={14} className="shrink-0 text-amberdark" />
                        {new Intl.NumberFormat("en-IN").format(p.size_sqft)} sq.ft
                      </p>
                    ) : null}
                    <p className="mt-4 font-display text-xl font-extrabold text-navy" data-testid={`property-price-${p.id}`}>
                      {p.price || "Price on enquiry"}
                    </p>
                    <a
                      href="/#contact-form"
                      data-testid={`property-enquire-${p.id}`}
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy px-5 py-2.5 text-sm font-bold text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
                    >
                      Enquire Now <ArrowRight size={14} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
